import * as THREE from "three";
import {
    IDENTITY_AXES,
    axesForPersist,
    axesToEulerDegrees,
    buildInstanceOrientationMatrix,
    cloneAxes,
    isCardinalAxes,
    normalizeAxesWithFaceFlip,
} from "./orientationAxes";
import {MM_TO_M, panelCornerMmToInstance} from "./panelMeshTransform";

export const isHandleLike = (obj) =>
    obj?.source === "3ds" || obj?.kind === "handle";

const isPanelInstance = (inst) =>
    inst && !isHandleLike(inst.detailData) && !isHandleLike(inst.detail);

export const isEmbeddedModuleInstance = (inst) =>
    Boolean(inst?.moduleSizeM && inst?.moduleKey != null);

/** Объём модуля на сцене (встраиваемый модуль или модуль композиции). */
export const isModuleShellInstance = (inst) => isEmbeddedModuleInstance(inst);

const isSnapTargetInstance = (inst) =>
    isPanelInstance(inst) || isEmbeddedModuleInstance(inst);

const axisEquals = (axis, expected) =>
    expected.every((value, index) => Math.abs((Number(axis?.[index]) || 0) - value) < 1e-8);

const getDetailSizeMm = (detailData) => ({
    length: Math.max(Number(detailData?.length) || 0, 1),
    width: Math.max(Number(detailData?.width) || 0, 1),
    height: Math.max(Number(detailData?.height) || 18, 1),
});

/**
 * После разворота TOP к камере сохраняем прежние границы размещения панели:
 * вертикальная — length вверх, фронтальная — length вправо.
 */
const getInitialOrientationCompensation = (axes, detailData) => {
    const {width, height} = getDetailSizeMm(detailData);
    const widthM = width * MM_TO_M;
    const heightM = height * MM_TO_M;

    const vertical = axisEquals(axes?.x, [0, 1, 0])
        && axisEquals(axes?.y, [1, 0, 0])
        && axisEquals(axes?.z, [0, 0, -1]);
    if (vertical) return {x: -heightM, y: 0, z: widthM};

    const frontal = axisEquals(axes?.x, [1, 0, 0])
        && axisEquals(axes?.y, [0, 0, 1])
        && axisEquals(axes?.z, [0, -1, 0]);
    if (frontal) return {x: 0, y: widthM, z: -heightM};

    return {x: 0, y: 0, z: 0};
};

/** Порог касания торцов (метры) — при отпускании мыши / размещении. */
export const SNAP_DISTANCE = 0.12;

/** Ручка к поверхности панели: чуть шире, чем стык панелей. */
export const HANDLE_SURFACE_SNAP_DISTANCE = 0.08;
export const HANDLE_SURFACE_SNAP_LIVE = 0.035;

/** Выравнивание начала/конца ребра и толщины при commit. */
export const SNAP_ALIGN_DISTANCE = 0.03;

/** Живой драг: короче порог, иначе стык «не отлипает». */
export const SNAP_LIVE_DISTANCE = 0.055;
export const SNAP_LIVE_ALIGN_DISTANCE = 0.02;

/** Модуль↔модуль (сцена композиции): крупнее объём — шире зона прилипания. */
export const MODULE_SHELL_SNAP_DISTANCE = 0.18;
export const MODULE_SHELL_SNAP_LIVE = 0.1;
export const MODULE_SHELL_SNAP_APPROACH = 0.22;

/** С какого расстояния (м) ручки начинают уменьшаться перед привязкой. */
export const SNAP_APPROACH_DISTANCE = 0.14;
export const SNAP_APPROACH_MIN_SCALE = 0.28;

/** Насколько проекции должны быть «рядом», чтобы ось участвовала в snap. */
export const SNAP_NEAR_PAD = 0.03;

/** При касании торца — подтягивать к краю только если уже почти у края (м). */
export const SNAP_EDGE_SEEK = 0.04;
export const SNAP_LIVE_EDGE_SEEK = 0.025;

const WORLD_AXES = [
    new THREE.Vector3(1, 0, 0),
    new THREE.Vector3(-1, 0, 0),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(0, -1, 0),
    new THREE.Vector3(0, 0, 1),
    new THREE.Vector3(0, 0, -1),
];

const snapComponent = (value) => {
    if (Math.abs(value) < 1e-8) return 0;
    if (Math.abs(Math.abs(value) - 1) < 1e-8) return Math.sign(value);
    return Number(value.toFixed(6));
};

const tuple = (vector) => [
    snapComponent(vector.x),
    snapComponent(vector.y),
    snapComponent(vector.z),
];

/** Округлить направление к ближайшей мировой оси ±X/±Y/±Z. */
export const snapVectorToWorldAxis = (vector) => {
    const v = vector.clone().normalize();
    let best = WORLD_AXES[0];
    let bestDot = -Infinity;
    for (const axis of WORLD_AXES) {
        const d = v.dot(axis);
        if (d > bestDot) {
            bestDot = d;
            best = axis;
        }
    }
    return best.clone();
};

/**
 * Базис ручки: local Z = наружу от поверхности (модель «торчит» по +Z).
 */
export const axesFromHandleMountNormal = (worldNormal) => {
    if (!worldNormal) {
        return cloneAxes(IDENTITY_AXES);
    }
    const outward = snapVectorToWorldAxis(
        worldNormal instanceof THREE.Vector3
            ? worldNormal
            : new THREE.Vector3(worldNormal.x, worldNormal.y, worldNormal.z),
    );
    let up = Math.abs(outward.y) < 0.9
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(1, 0, 0);
    const x = new THREE.Vector3().crossVectors(up, outward).normalize();
    if (x.lengthSq() < 1e-8) {
        up = new THREE.Vector3(0, 0, 1);
        x.crossVectors(up, outward).normalize();
    }
    const y = new THREE.Vector3().crossVectors(outward, x).normalize();
    return {
        x: tuple(x),
        y: tuple(y),
        z: tuple(outward),
    };
};

/**
 * Базис панели: local Y = толщина (нормаль).
 * mode "cube" — толщина внутрь объёма (−outward);
 * mode "detail" | "free" — толщина вдоль outward.
 */
export const axesFromSurfaceNormal = (worldNormal, mode = "detail") => {
    if (!worldNormal) {
        return cloneAxes(IDENTITY_AXES);
    }

    const outward = snapVectorToWorldAxis(
        worldNormal instanceof THREE.Vector3
            ? worldNormal
            : new THREE.Vector3(worldNormal.x, worldNormal.y, worldNormal.z),
    );
    const thickness = mode === "cube" ? outward.clone().negate() : outward;

    let reference = Math.abs(thickness.y) < 0.9
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(1, 0, 0);

    const x = new THREE.Vector3().crossVectors(reference, thickness).normalize();
    if (x.lengthSq() < 1e-8) {
        reference = new THREE.Vector3(0, 0, 1);
        x.crossVectors(reference, thickness).normalize();
    }
    const z = new THREE.Vector3().crossVectors(thickness, x).normalize();

    return {
        x: tuple(x),
        y: tuple(thickness),
        z: tuple(z),
    };
};

const MM_TO_M_MODULE = 0.001;
const FALLBACK_SIZE_M = 1;

/**
 * Размер ориентира модуля в метрах.
 * @param {number|{width?:number,height?:number,depth?:number}|null} size
 *   number — куб (м); object — габариты уже в метрах (width→X, height→Y, depth→Z).
 */
export const normalizeModuleSizeM = (size) => {
    if (size != null && typeof size === "object") {
        return {
            width: Math.max(Number(size.width) || FALLBACK_SIZE_M, 0.05),
            height: Math.max(Number(size.height) || FALLBACK_SIZE_M, 0.05),
            depth: Math.max(Number(size.depth) || FALLBACK_SIZE_M, 0.05),
        };
    }
    const s = Math.max(Number(size) || FALLBACK_SIZE_M, 0.05);
    return {width: s, height: s, depth: s};
};

/** Габариты модуля с бэка (мм) → размер ориентира в метрах. */
export const moduleDimensionsMmToSizeM = (widthMm, heightMm, depthMm) => {
    const toM = (mm) => {
        const n = Number(mm);
        return (Number.isFinite(n) && n > 0 ? n : 1000) * MM_TO_M_MODULE;
    };
    return normalizeModuleSizeM({
        width: toM(widthMm),
        height: toM(heightMm),
        depth: toM(depthMm),
    });
};

export const getCubeOrigin = (size) => {
    const {width, depth} = normalizeModuleSizeM(size);
    return {
        x: -width / 2,
        y: 0,
        z: -depth / 2,
    };
};

/**
 * World AABB зелёного контура модуля (ReferenceCube).
 * Центр куба — (0, height/2, 0), габариты width×height×depth.
 */
export const getModuleCubeWorldAabb = (size) => {
    const {width, height, depth} = normalizeModuleSizeM(size);
    return {
        min: {x: -width / 2, y: 0, z: -depth / 2},
        max: {x: width / 2, y: height, z: depth / 2},
    };
};

export const cubeLocalToWorld = (local, size) => {
    const origin = getCubeOrigin(size);
    return {
        x: origin.x + local.x,
        y: origin.y + local.y,
        z: origin.z + local.z,
    };
};

export const worldPointToCubeLocal = (point, size) => {
    const origin = getCubeOrigin(size);
    return {
        x: point.x - origin.x,
        y: point.y - origin.y,
        z: point.z - origin.z,
    };
};

/**
 * Режим «Орто»: движение только вдоль одной мировой оси (X или Y или Z).
 * Диагонали (сдвиг сразу по двум/трём осям) отключаются.
 */
export const getDominantOrthoAxis = (startLocal, currentLocal) => {
    const sx = Number(startLocal?.x) || 0;
    const sy = Number(startLocal?.y) || 0;
    const sz = Number(startLocal?.z) || 0;
    const cx = Number(currentLocal?.x) || 0;
    const cy = Number(currentLocal?.y) || 0;
    const cz = Number(currentLocal?.z) || 0;
    const dx = Math.abs(cx - sx);
    const dy = Math.abs(cy - sy);
    const dz = Math.abs(cz - sz);
    if (dx >= dy && dx >= dz) return "x";
    if (dy >= dx && dy >= dz) return "y";
    return "z";
};

/** Ось гизмо TransformControls ('X'|'Y'|'Z'|'XY'|…) → 'x'|'y'|'z'|null */
export const orthoAxisFromTransformControls = (tcAxis) => {
    if (tcAxis === "X" || tcAxis === "Y" || tcAxis === "Z") {
        return tcAxis.toLowerCase();
    }
    return null;
};

export const projectLocalToOrthoAxis = (startLocal, currentLocal, axis) => {
    const sx = Number(startLocal?.x) || 0;
    const sy = Number(startLocal?.y) || 0;
    const sz = Number(startLocal?.z) || 0;
    const cx = Number(currentLocal?.x) || 0;
    const cy = Number(currentLocal?.y) || 0;
    const cz = Number(currentLocal?.z) || 0;
    const locked = axis || getDominantOrthoAxis(startLocal, currentLocal);
    return {
        x: locked === "x" ? cx : sx,
        y: locked === "y" ? cy : sy,
        z: locked === "z" ? cz : sz,
    };
};

/** @deprecated use projectLocalToOrthoAxis */
export const constrainLocalToOrthoAxes = (startLocal, currentLocal) => (
    projectLocalToOrthoAxis(startLocal, currentLocal, null)
);

/** Нормаль из pointer-события R3F/Three. */
export const worldNormalFromEvent = (event) => {
    if (!event?.face || !event?.object) return null;
    return event.face.normal.clone().transformDirection(event.object.matrixWorld).normalize();
};

/** Габариты ручки (мм): длина × высота × глубина (локальные X/Y/Z). */
export const getHandleSizeMm = (detailData) => {
    const size = Math.max(
        Number(detailData?.sizeMm) || Number(detailData?.length) || 120,
        1,
    );
    return {
        length: size,
        width: Math.max(Number(detailData?.width) || size * 0.25, 1),
        height: Math.max(Number(detailData?.height) || size * 0.25, 1),
    };
};

const emptyAabb = () => ({
    min: {x: Infinity, y: Infinity, z: Infinity},
    max: {x: -Infinity, y: -Infinity, z: -Infinity},
});

const expandAabbPoint = (aabb, v) => {
    aabb.min.x = Math.min(aabb.min.x, v.x);
    aabb.min.y = Math.min(aabb.min.y, v.y);
    aabb.min.z = Math.min(aabb.min.z, v.z);
    aabb.max.x = Math.max(aabb.max.x, v.x);
    aabb.max.y = Math.max(aabb.max.y, v.y);
    aabb.max.z = Math.max(aabb.max.z, v.z);
};

/**
 * World AABB ручки: модель центрирована в origin инстанса (без rotateX панелей).
 */
export const getHandleWorldAabb = (inst, cubeSize) => {
    const {length, width, height} = getHandleSizeMm(inst?.detailData);
    const s = MM_TO_M;
    const hx = (length * s) / 2;
    const hy = (width * s) / 2;
    const hz = (height * s) / 2;
    const basis = buildInstanceOrientationMatrix(
        inst?.axes || IDENTITY_AXES,
        Boolean(inst?.faceFlip),
    );
    const worldPos = cubeLocalToWorld(inst.localPosition || {x: 0, y: 0, z: 0}, cubeSize);
    const origin = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z);
    const aabb = emptyAabb();

    for (const x of [-hx, hx]) {
        for (const y of [-hy, hy]) {
            for (const z of [-hz, hz]) {
                const v = new THREE.Vector3(x, y, z).applyMatrix4(basis).add(origin);
                expandAabbPoint(aabb, v);
            }
        }
    }
    return aabb;
};

/**
 * World AABB панели: rotateX(−π/2), перенос по Z на ширину → базис.
 * Локально как у куба: +X/+Y/+Z из угла внутрь.
 */
export const getPanelWorldAabb = (inst, cubeSize) => {
    const {length, width, height} = getDetailSizeMm(inst?.detailData);
    const basis = buildInstanceOrientationMatrix(
        inst?.axes || IDENTITY_AXES,
        Boolean(inst?.faceFlip),
    );
    const worldPos = cubeLocalToWorld(inst.localPosition || {x: 0, y: 0, z: 0}, cubeSize);
    const origin = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z);
    const aabb = emptyAabb();

    const cornersMm = [
        [0, 0, 0],
        [length, 0, 0],
        [0, width, 0],
        [length, width, 0],
        [0, 0, height],
        [length, 0, height],
        [0, width, height],
        [length, width, height],
    ];

    for (const [x, y, z] of cornersMm) {
        const v = panelCornerMmToInstance(x, y, z, MM_TO_M, width);
        v.applyMatrix4(basis);
        v.add(origin);
        expandAabbPoint(aabb, v);
    }

    return aabb;
};

/** World AABB встраиваемого модуля: (0,0,0) — угол, +X/+Y/+Z внутрь объёма. */
export const getEmbeddedModuleWorldAabb = (inst, cubeSize) => {
    const {width, height, depth} = normalizeModuleSizeM(inst?.moduleSizeM);
    const basis = buildInstanceOrientationMatrix(
        inst?.axes || IDENTITY_AXES,
        Boolean(inst?.faceFlip),
    );
    const worldPos = cubeLocalToWorld(inst.localPosition || {x: 0, y: 0, z: 0}, cubeSize);
    const origin = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z);
    const aabb = emptyAabb();
    const corners = [
        [0, 0, 0],
        [width, 0, 0],
        [0, height, 0],
        [width, height, 0],
        [0, 0, depth],
        [width, 0, depth],
        [0, height, depth],
        [width, height, depth],
    ];
    for (const [x, y, z] of corners) {
        const v = new THREE.Vector3(x, y, z).applyMatrix4(basis).add(origin);
        expandAabbPoint(aabb, v);
    }
    return aabb;
};

/**
 * Локальные углы инстанса (м) — куда ставить ручки перетаскивания.
 * Панель: 8 углов из (0,0,0); ручка: 8 углов центрированного бокса.
 */
export const getInstanceLocalCornersM = (inst) => {
    if (isModuleShellInstance(inst)) {
        const {width, height, depth} = normalizeModuleSizeM(inst.moduleSizeM);
        return [
            [0, 0, 0],
            [width, 0, 0],
            [0, height, 0],
            [width, height, 0],
            [0, 0, depth],
            [width, 0, depth],
            [0, height, depth],
            [width, height, depth],
        ];
    }

    if (isHandleLike(inst?.detailData) || isHandleLike(inst?.detail)) {
        const {length, width, height} = getHandleSizeMm(inst?.detailData);
        const hx = (length * MM_TO_M) / 2;
        const hy = (width * MM_TO_M) / 2;
        const hz = (height * MM_TO_M) / 2;
        const corners = [];
        for (const x of [-hx, hx]) {
            for (const y of [-hy, hy]) {
                for (const z of [-hz, hz]) {
                    corners.push([x, y, z]);
                }
            }
        }
        return corners;
    }

    const {length, width, height} = getDetailSizeMm(inst?.detailData);
    return [
        [0, 0, 0],
        [length, 0, 0],
        [0, width, 0],
        [length, width, 0],
        [0, 0, height],
        [length, 0, height],
        [0, width, height],
        [length, width, height],
    ].map(([x, y, z]) => {
        const v = panelCornerMmToInstance(x, y, z, MM_TO_M, width);
        return [v.x, v.y, v.z];
    });
};

const midPoint3 = (a, b) => [
    (a[0] + b[0]) / 2,
    (a[1] + b[1]) / 2,
    (a[2] + b[2]) / 2,
];

const faceCenter3 = (indices, corners) => {
    const n = indices.length;
    return [
        indices.reduce((s, i) => s + corners[i][0], 0) / n,
        indices.reduce((s, i) => s + corners[i][1], 0) / n,
        indices.reduce((s, i) => s + corners[i][2], 0) / n,
    ];
};

/**
 * Точки захвата для свободного поворота: углы, середины рёбер и центры граней (локальные м).
 */
export const getPanelRotationGripPointsM = (inst) => {
    const corners = getInstanceLocalCornersM(inst);
    if (corners.length !== 8) {
        return corners.map((pos, index) => ({
            key: `corner-${index}`,
            pos,
            kind: "corner",
            isBase: false,
        }));
    }

    const edgePairs = [
        [0, 1], [0, 2], [0, 4], [1, 3], [1, 5], [2, 3], [2, 6], [3, 7],
        [4, 5], [4, 6], [5, 7], [6, 7],
    ];
    const faceIndices = [
        [0, 1, 2, 3],
        [4, 5, 6, 7],
        [0, 2, 4, 6],
        [1, 3, 5, 7],
        [0, 1, 4, 5],
        [2, 3, 6, 7],
    ];

    const grips = [];
    const seen = new Set();
    const add = (key, pos, kind, isBase = false) => {
        const sig = pos.map((v) => v.toFixed(5)).join(",");
        if (seen.has(sig)) return;
        seen.add(sig);
        grips.push({key, pos, kind, isBase});
    };

    corners.forEach((pos, index) => {
        add(`corner-${index}`, pos, "corner", isOriginCornerLocal(pos));
    });
    edgePairs.forEach(([a, b], index) => {
        add(`edge-${index}`, midPoint3(corners[a], corners[b]), "edge", false);
    });
    faceIndices.forEach((indices, index) => {
        add(`face-${index}`, faceCenter3(indices, corners), "face", false);
    });

    return grips;
};

const isOriginCornerLocal = (pos) => (
    Math.abs(pos?.[0] || 0) < 1e-6
    && Math.abs(pos?.[1] || 0) < 1e-6
    && Math.abs(pos?.[2] || 0) < 1e-6
);

const INSTANCE_BOX_FACE_INDICES = [
    [0, 1, 2, 3],
    [4, 5, 6, 7],
    [0, 2, 4, 6],
    [1, 3, 5, 7],
    [0, 1, 4, 5],
    [2, 3, 6, 7],
];

/** Локальная точка (м) → мир; базис как у applyAxesToObject. */
export const instanceLocalPointToWorld = (inst, localPoint, cubeSize) => {
    const matrix = buildInstanceOrientationMatrix(
        inst?.axes || IDENTITY_AXES,
        Boolean(inst?.faceFlip),
    );
    const worldPos = cubeLocalToWorld(inst?.localPosition || {x: 0, y: 0, z: 0}, cubeSize);
    const origin = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z);
    const tuple = Array.isArray(localPoint) ? localPoint : [localPoint?.x, localPoint?.y, localPoint?.z];
    return new THREE.Vector3(
        Number(tuple[0]) || 0,
        Number(tuple[1]) || 0,
        Number(tuple[2]) || 0,
    ).applyMatrix4(matrix).add(origin);
};

/** Шесть граней oriented box в world (панель / модуль-сосед). */
export const getInstanceWorldFaces = (inst, cubeSize) => {
    if (isHandleLike(inst?.detailData) || isHandleLike(inst?.detail)) {
        return [];
    }
    const cornersLocal = getInstanceLocalCornersM(inst);
    if (cornersLocal.length !== 8) {
        return [];
    }

    const matrix = buildInstanceOrientationMatrix(
        inst?.axes || IDENTITY_AXES,
        Boolean(inst?.faceFlip),
    );
    const worldPos = cubeLocalToWorld(inst?.localPosition || {x: 0, y: 0, z: 0}, cubeSize);
    const origin = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z);
    const worldCorners = cornersLocal.map(([x, y, z]) => (
        new THREE.Vector3(x, y, z).applyMatrix4(matrix).add(origin)
    ));

    const center = new THREE.Vector3();
    for (const corner of worldCorners) {
        center.add(corner);
    }
    center.multiplyScalar(1 / worldCorners.length);

    return INSTANCE_BOX_FACE_INDICES.map((indices) => {
        const pts = indices.map((i) => worldCorners[i]);
        const edgeA = pts[1].clone().sub(pts[0]);
        const edgeB = pts[3].clone().sub(pts[0]);
        const normal = new THREE.Vector3().crossVectors(edgeA, edgeB).normalize();
        if (normal.lengthSq() < 1e-12) return null;
        const faceCenter = new THREE.Vector3();
        for (const p of pts) faceCenter.add(p);
        faceCenter.multiplyScalar(1 / pts.length);
        if (normal.dot(faceCenter.clone().sub(center)) < 0) {
            normal.negate();
        }
        return {corners: pts, normal};
    }).filter((face) => face && face.normal.lengthSq() > 1e-12);
};

/** AABB инстанса: ручка, панель или встраиваемый модуль. */
export const getInstanceWorldAabb = (inst, cubeSize) => {
    if (isEmbeddedModuleInstance(inst)) {
        return getEmbeddedModuleWorldAabb(inst, cubeSize);
    }
    if (isHandleLike(inst?.detailData) || isHandleLike(inst?.detail)) {
        return getHandleWorldAabb(inst, cubeSize);
    }
    return getPanelWorldAabb(inst, cubeSize);
};

/**
 * Только касание пластей (без rim/edge) — ручка «липнет» к поверхности, не к ребру.
 * Важно: липнем только к грани «сзади» по нормали монтажа (axes.z / outward),
 * иначе при пересечении тонкой панели ближайшая грань прыгает на противоположную → дрожание.
 *
 * @param {object} aabb world AABB ручки
 * @param {object[]} panels world AABB панелей
 * @param {number} threshold
 * @param {{outward?: {x:number,y:number,z:number}|number[]|null}} [options]
 */
export const snapHandleAabbToPanelSurfaces = (
    aabb,
    panels = [],
    threshold = HANDLE_SURFACE_SNAP_DISTANCE,
    options = {},
) => {
    if (!aabb || !panels.length) {
        return {dx: 0, dy: 0, dz: 0};
    }

    const outward = normalizeOutward(options.outward);
    const primary = primaryAxisFromOutward(outward);
    // Без устойчивой нормали — не угадываем «противоположную» стенку.
    if (!primary) {
        return {dx: 0, dy: 0, dz: 0};
    }

    const sign = outward[primary] >= 0 ? 1 : -1;
    // Тангенциальное перекрытие по двум осям грани (без большого pad — иначе ловим боковые стенки).
    const facePad = Math.min(SNAP_NEAR_PAD * 0.35, 0.01);

    let bestDelta = null;

    for (const o of panels) {
        if (!o?.min || !o?.max) continue;
        if (!handleOverlapsPanelFace(aabb, o, primary, facePad)) continue;

        const delta = mountFaceContactDelta(aabb, o, primary, sign);
        if (!Number.isFinite(delta)) continue;
        if (Math.abs(delta) > threshold) continue;
        // Игнор микросдвигов — убирает дрожание на уже прилипшей ручке.
        if (Math.abs(delta) < 1e-5) continue;
        bestDelta = takeCloserDelta(bestDelta, delta);
    }

    const dx = primary === "x" ? (bestDelta ?? 0) : 0;
    const dy = primary === "y" ? (bestDelta ?? 0) : 0;
    const dz = primary === "z" ? (bestDelta ?? 0) : 0;
    return {dx, dy, dz};
};

const normalizeOutward = (outward) => {
    if (!outward) return null;
    if (Array.isArray(outward)) {
        return {
            x: Number(outward[0]) || 0,
            y: Number(outward[1]) || 0,
            z: Number(outward[2]) || 0,
        };
    }
    return {
        x: Number(outward.x) || 0,
        y: Number(outward.y) || 0,
        z: Number(outward.z) || 0,
    };
};

/** Ось мира, вдоль которой смотрит нормаль монтажа ручки. */
const primaryAxisFromOutward = (outward) => {
    if (!outward) return null;
    const ax = Math.abs(outward.x);
    const ay = Math.abs(outward.y);
    const az = Math.abs(outward.z);
    const max = Math.max(ax, ay, az);
    if (max < 0.5) return null;
    if (ax >= ay && ax >= az) return "x";
    if (ay >= ax && ay >= az) return "y";
    return "z";
};

const otherAxes = (primary) => {
    if (primary === "x") return ["y", "z"];
    if (primary === "y") return ["x", "z"];
    return ["x", "y"];
};

/** Проекция ручки пересекается с гранью панели в плоскости грани. */
const handleOverlapsPanelFace = (aabb, panel, primary, pad) => {
    const [a, b] = otherAxes(primary);
    return rangesClose(aabb.min[a], aabb.max[a], panel.min[a], panel.max[a], pad)
        && rangesClose(aabb.min[b], aabb.max[b], panel.min[b], panel.max[b], pad);
};

/**
 * Контакт с гранью панели, от которой направлена нормаль наружу.
 * outward +X → касание max-X панели (ручка снаружи с +X);
 * outward −X → касание min-X.
 */
const mountFaceContactDelta = (aabb, panel, primary, sign) => {
    if (sign >= 0) {
        return panel.max[primary] - aabb.min[primary];
    }
    return panel.min[primary] - aabb.max[primary];
};

const bestSnapDeltaOrNull = (candidates, threshold) => {
    let found = false;
    let best = 0;
    let bestAbs = threshold + 1e-9;
    for (const d of candidates) {
        if (!Number.isFinite(d)) continue;
        const a = Math.abs(d);
        if (a <= threshold && a < bestAbs) {
            found = true;
            bestAbs = a;
            best = d;
        }
    }
    return found ? best : null;
};

/** Проекции пересекаются или почти соприкасаются (с запасом pad). */
const rangesClose = (amin, amax, bmin, bmax, pad) => {
    if (amin <= bmax + pad && bmin <= amax + pad) return true;
    const gap = amin > bmax ? amin - bmax : bmin - amax;
    return gap <= pad;
};

/**
 * Стыковки по одной оси:
 * contact — касание пластей;
 * edge — к краю соседа (начало/конец ребра или толщина), не к середине торца.
 * rim — только совмещение мин/макс (предпочтительно при уже установленном контакте).
 */
const axisSnapCandidates = (amin, amax, bmin, bmax) => ({
    contact: [bmin - amax, bmax - amin],
    rim: [bmin - amin, bmax - amax],
    edge: [
        bmin - amin,
        bmax - amax,
        bmax - amin,
        bmin - amax,
    ],
});

const takeCloserDelta = (current, next) => {
    if (next == null || !Number.isFinite(next)) return current;
    if (current == null || !Number.isFinite(current)) return next;
    return Math.abs(next) < Math.abs(current) ? next : current;
};

const resolveActiveNodeSnapThreshold = (inst, threshold, mode) => {
    if (!isEmbeddedModuleInstance(inst)) {
        return mode === "live"
            ? Math.min(threshold, SNAP_LIVE_DISTANCE)
            : threshold;
    }
    return mode === "live"
        ? MODULE_SHELL_SNAP_LIVE
        : MODULE_SHELL_SNAP_DISTANCE;
};

const activeNodeWorldPoint = (trial, activeNodeLocal, cubeSize) => {
    if (!Array.isArray(activeNodeLocal) || activeNodeLocal.length < 3) return null;
    return instanceLocalPointToWorld(trial, activeNodeLocal, cubeSize);
};

const pointInsideConvexQuad = (point, quad, normal, pad) => {
    for (let i = 0; i < quad.length; i += 1) {
        const a = quad[i];
        const b = quad[(i + 1) % quad.length];
        const edge = b.clone().sub(a);
        const toPoint = point.clone().sub(a);
        if (edge.cross(toPoint).dot(normal) < -pad) {
            return false;
        }
    }
    return true;
};

const snapDeltaToOrientedFace = (point, face, threshold, pad) => {
    const normal = face.normal;
    if (!normal || normal.lengthSq() < 1e-12) return null;
    const anchor = face.corners[0];
    const dist = point.clone().sub(anchor).dot(normal);
    if (Math.abs(dist) > threshold) return null;
    const projected = point.clone().addScaledVector(normal, -dist);
    if (!pointInsideConvexQuad(projected, face.corners, normal, pad)) {
        return null;
    }
    return {
        dx: -dist * normal.x,
        dy: -dist * normal.y,
        dz: -dist * normal.z,
        mag: Math.abs(dist),
    };
};

/** Панель на 90° — axis-AABB; с наклоном — грани OBB. */
const snapActiveNodeToPanelNeighbor = (point, inst, cubeSize, threshold, panelPad, facePad) => {
    if (!isCardinalAxes(inst?.axes || IDENTITY_AXES)) {
        let best = null;
        for (const face of getInstanceWorldFaces(inst, cubeSize)) {
            const candidate = snapDeltaToOrientedFace(point, face, threshold, facePad);
            if (!candidate) continue;
            if (!best || candidate.mag < best.mag) best = candidate;
        }
        return best;
    }
    return snapActiveNodeToModuleAabb(
        point,
        getInstanceWorldAabb(inst, cubeSize),
        threshold,
        panelPad,
    );
};

const snapActiveNodeToModuleAabb = (point, target, threshold, pad) => {
    if (!target?.min || !target?.max) return null;
    const nearX = point.x >= target.min.x - pad && point.x <= target.max.x + pad;
    const nearY = point.y >= target.min.y - pad && point.y <= target.max.y + pad;
    const nearZ = point.z >= target.min.z - pad && point.z <= target.max.z + pad;

    let dx = null;
    let dy = null;
    let dz = null;

    if (nearY && nearZ) {
        dx = takeCloserDelta(dx, bestSnapDeltaOrNull([
            target.min.x - point.x,
            target.max.x - point.x,
        ], threshold));
    }
    if (nearX && nearZ) {
        dy = takeCloserDelta(dy, bestSnapDeltaOrNull([
            target.min.y - point.y,
            target.max.y - point.y,
        ], threshold));
    }
    if (nearX && nearY) {
        dz = takeCloserDelta(dz, bestSnapDeltaOrNull([
            target.min.z - point.z,
            target.max.z - point.z,
        ], threshold));
    }

    if (dx == null && dy == null && dz == null) return null;
    const fx = dx ?? 0;
    const fy = dy ?? 0;
    const fz = dz ?? 0;
    return {dx: fx, dy: fy, dz: fz, mag: Math.hypot(fx, fy, fz)};
};

/**
 * Узел → плоскости граней соседей; куб модуля — по-прежнему axis-AABB.
 */
const snapActiveNodeAgainstNeighbors = (
    point,
    {moduleAabb = null, neighborInstances = []},
    cubeSize,
    threshold,
) => {
    if (!point) return {dx: 0, dy: 0, dz: 0};
    const panelPad = Math.max(SNAP_NEAR_PAD, threshold * 0.28);
    const modulePad = Math.max(SNAP_NEAR_PAD, threshold * 0.38);
    let best = null;

    const consider = (candidate) => {
        if (!candidate) return;
        if (!best || candidate.mag < best.mag) {
            best = candidate;
        }
    };

    for (const inst of neighborInstances) {
        if (!inst) continue;

        if (isPanelInstance(inst)) {
            const facePad = Math.min(panelPad, SNAP_NEAR_PAD + 0.015);
            consider(snapActiveNodeToPanelNeighbor(
                point,
                inst,
                cubeSize,
                threshold,
                panelPad,
                facePad,
            ));
            continue;
        }

        // Модули на сцене композиции: грани OBB + запасной AABB.
        if (isEmbeddedModuleInstance(inst)) {
            const neighborAabb = getInstanceWorldAabb(inst, cubeSize);
            const facePad = Math.min(modulePad, SNAP_NEAR_PAD + 0.015);
            for (const face of getInstanceWorldFaces(inst, cubeSize)) {
                consider(snapDeltaToOrientedFace(point, face, threshold, facePad));
            }
            consider(snapActiveNodeToModuleAabb(
                point,
                neighborAabb,
                threshold,
                modulePad,
            ));
        }
    }

    if (moduleAabb) {
        const candidate = snapActiveNodeToModuleAabb(point, moduleAabb, threshold, panelPad);
        const panelNeighbors = neighborInstances.some(isPanelInstance);
        // Когда рядом другая панель — не уводить угол на внутреннюю грань куба модуля.
        if (candidate && (!panelNeighbors || !best)) {
            consider(candidate);
        }
    }

    if (!best) return {dx: 0, dy: 0, dz: 0};
    return {dx: best.dx, dy: best.dy, dz: best.dz};
};

const closestFaceApproach = (point, aabb, maxDist) => {
    if (!point || !aabb?.min || !aabb?.max) return null;
    const pad = Math.max(SNAP_NEAR_PAD, maxDist);
    const nearX = point.x >= aabb.min.x - pad && point.x <= aabb.max.x + pad;
    const nearY = point.y >= aabb.min.y - pad && point.y <= aabb.max.y + pad;
    const nearZ = point.z >= aabb.min.z - pad && point.z <= aabb.max.z + pad;

    let bestDist = Infinity;
    let target = null;

    const consider = (dist, tx, ty, tz) => {
        if (!Number.isFinite(dist) || dist > maxDist || dist >= bestDist) return;
        bestDist = dist;
        target = new THREE.Vector3(tx, ty, tz);
    };

    if (nearY && nearZ) {
        consider(Math.abs(aabb.min.x - point.x), aabb.min.x, point.y, point.z);
        consider(Math.abs(aabb.max.x - point.x), aabb.max.x, point.y, point.z);
    }
    if (nearX && nearZ) {
        consider(Math.abs(aabb.min.y - point.y), point.x, aabb.min.y, point.z);
        consider(Math.abs(aabb.max.y - point.y), point.x, aabb.max.y, point.z);
    }
    if (nearX && nearY) {
        consider(Math.abs(aabb.min.z - point.z), point.x, point.y, aabb.min.z);
        consider(Math.abs(aabb.max.z - point.z), point.x, point.y, aabb.max.z);
    }

    if (!target || !Number.isFinite(bestDist)) return null;
    return {dist: bestDist, target};
};

/**
 * Превью сближения до snap: точка, за которую тянем, и точка на соседней детали.
 * Считается по несняпнутой позе — уменьшение видно до прилипания.
 */
export const getSnapApproachPreview = ({
    inst,
    localPosition,
    axes,
    others = [],
    cubeSize,
    activeNodeLocal = null,
    approachDistance = isEmbeddedModuleInstance(inst)
        ? MODULE_SHELL_SNAP_APPROACH
        : SNAP_APPROACH_DISTANCE,
    snapDistance = isEmbeddedModuleInstance(inst)
        ? MODULE_SHELL_SNAP_LIVE
        : SNAP_LIVE_DISTANCE,
} = {}) => {
    if (!activeNodeLocal || !cubeSize) return null;
    const trial = {
        ...inst,
        localPosition,
        axes: axes || inst?.axes || IDENTITY_AXES,
    };
    const point = activeNodeWorldPoint(trial, activeNodeLocal, cubeSize);
    if (!point) return null;

    const panels = (Array.isArray(others) ? others : [])
        .filter(isSnapTargetInstance)
        .filter((o) => o && o.instanceId !== inst?.instanceId);

    let best = null;
    for (const panel of panels) {
        const hit = closestFaceApproach(
            point,
            getInstanceWorldAabb(panel, cubeSize),
            approachDistance,
        );
        if (!hit) continue;
        if (!best || hit.dist < best.dist) {
            best = {dist: hit.dist};
        }
    }

    if (!best) return null;

    const span = Math.max(approachDistance - snapDistance, 1e-6);
    const t = THREE.MathUtils.clamp((approachDistance - best.dist) / span, 0, 1);
    const scale = 1 - (1 - SNAP_APPROACH_MIN_SCALE) * t;

    return {
        scale,
        dist: best.dist,
        sourceLocal: [...activeNodeLocal],
        sourceInstanceId: inst?.instanceId ?? null,
    };
};

/**
 * Сдвиги dx/dy/dz к другим AABB.
 * Касание пластей (flush). Rim к краю — только если уже близко к краю
 * (можно стыковать к середине торца).
 */
export const snapAabbAgainstOthers = (
    aabb,
    others = [],
    threshold = SNAP_DISTANCE,
    alignThreshold = SNAP_ALIGN_DISTANCE,
    options = {},
) => {
    if (!aabb || !others.length) {
        return {dx: 0, dy: 0, dz: 0};
    }

    const nearPad = Math.max(SNAP_NEAR_PAD, 0.02);
    const alignLim = Math.min(alignThreshold, threshold);
    const seekEdges = options.seekEdges !== false;
    const edgeSeek = seekEdges
        ? Math.max(alignLim, options.edgeSeek ?? SNAP_EDGE_SEEK)
        : alignLim;

    let dx = null;
    let dy = null;
    let dz = null;

    for (const o of others) {
        if (!o?.min || !o?.max) continue;

        const nearYZ =
            rangesClose(aabb.min.y, aabb.max.y, o.min.y, o.max.y, nearPad)
            && rangesClose(aabb.min.z, aabb.max.z, o.min.z, o.max.z, nearPad);
        const nearXZ =
            rangesClose(aabb.min.x, aabb.max.x, o.min.x, o.max.x, nearPad)
            && rangesClose(aabb.min.z, aabb.max.z, o.min.z, o.max.z, nearPad);
        const nearXY =
            rangesClose(aabb.min.x, aabb.max.x, o.min.x, o.max.x, nearPad)
            && rangesClose(aabb.min.y, aabb.max.y, o.min.y, o.max.y, nearPad);

        const cx = axisSnapCandidates(aabb.min.x, aabb.max.x, o.min.x, o.max.x);
        const cy = axisSnapCandidates(aabb.min.y, aabb.max.y, o.min.y, o.max.y);
        const cz = axisSnapCandidates(aabb.min.z, aabb.max.z, o.min.z, o.max.z);

        const contactX = nearYZ ? bestSnapDeltaOrNull(cx.contact, threshold) : null;
        const contactY = nearXZ ? bestSnapDeltaOrNull(cy.contact, threshold) : null;
        const contactZ = nearXY ? bestSnapDeltaOrNull(cz.contact, threshold) : null;

        if (contactX != null) dx = takeCloserDelta(dx, contactX);
        if (contactY != null) dy = takeCloserDelta(dy, contactY);
        if (contactZ != null) dz = takeCloserDelta(dz, contactZ);

        if (contactX == null && nearYZ) {
            dx = takeCloserDelta(dx, bestSnapDeltaOrNull(cx.edge, alignLim));
        }
        if (contactY == null && nearXZ) {
            dy = takeCloserDelta(dy, bestSnapDeltaOrNull(cy.edge, alignLim));
        }
        if (contactZ == null && nearXY) {
            dz = takeCloserDelta(dz, bestSnapDeltaOrNull(cz.edge, alignLim));
        }

        // Rim только в малом окне у края — середина торца остаётся свободной.
        if (seekEdges) {
            if (contactX != null) {
                dy = takeCloserDelta(dy, bestSnapDeltaOrNull(cy.rim, edgeSeek));
                dz = takeCloserDelta(dz, bestSnapDeltaOrNull(cz.rim, edgeSeek));
            }
            if (contactY != null) {
                dx = takeCloserDelta(dx, bestSnapDeltaOrNull(cx.rim, edgeSeek));
                dz = takeCloserDelta(dz, bestSnapDeltaOrNull(cz.rim, edgeSeek));
            }
            if (contactZ != null) {
                dx = takeCloserDelta(dx, bestSnapDeltaOrNull(cx.rim, edgeSeek));
                dy = takeCloserDelta(dy, bestSnapDeltaOrNull(cy.rim, edgeSeek));
            }
        }
    }

    return {
        dx: dx ?? 0,
        dy: dy ?? 0,
        dz: dz ?? 0,
    };
};

/**
 * Притянуть localPosition инстанса к контуру модуля и другим деталям (по world AABB).
 * Ручки — к поверхностям панелей и внутренним граням куба модуля.
 * @param {object} options
 * @param {'commit'|'live'} [options.mode]
 * @param {{x:number,y:number,z:number}|null} [options.previousLocalPosition]
 *        Для live: если пользователь тянет от стыка — не притягивать обратно по этой оси.
 */
export const snapLocalToOtherDetails = ({
    inst,
    localPosition,
    axes,
    others = [],
    cubeSize,
    threshold = SNAP_DISTANCE,
    alignThreshold = SNAP_ALIGN_DISTANCE,
    mode = "commit",
    previousLocalPosition = null,
    /** Локальная координата узла, за который пользователь тянет деталь. */
    activeNodeLocal = null,
    /** При размещении по клику — без дальнего rim (иначе улетает к соседу). */
    seekEdges = null,
}) => {
    const snapOthers = (Array.isArray(others) ? others : []).filter(isSnapTargetInstance);
    const neighborInstances = snapOthers.filter((o) => o && o.instanceId !== inst.instanceId);
    const moduleAabb = cubeSize ? getModuleCubeWorldAabb(cubeSize) : null;
    const panelAabbs = neighborInstances.map((o) => getInstanceWorldAabb(o, cubeSize));
    // Контур модуля — всегда в целях стыковки (пол / стенки / потолок).
    const snapTargets = moduleAabb
        ? [moduleAabb, ...panelAabbs]
        : panelAabbs;

    if (!moduleAabb && neighborInstances.length === 0) {
        return localPosition;
    }

    const handle = isHandleLike(inst?.detailData) || isHandleLike(inst?.detail);
    const trial = {
        ...inst,
        localPosition,
        axes: axes || inst.axes || IDENTITY_AXES,
        detailData: inst.detailData,
    };
    const aabb = getInstanceWorldAabb(trial, cubeSize);

    let dx;
    let dy;
    let dz;

    if (activeNodeLocal) {
        const activePoint = activeNodeWorldPoint(trial, activeNodeLocal, cubeSize);
        const nodeThreshold = resolveActiveNodeSnapThreshold(inst, threshold, mode);
        ({dx, dy, dz} = snapActiveNodeAgainstNeighbors(
            activePoint,
            {moduleAabb, neighborInstances},
            cubeSize,
            nodeThreshold,
        ));
    } else if (handle) {
        const contactTh = mode === "live"
            ? Math.min(threshold, HANDLE_SURFACE_SNAP_LIVE)
            : Math.max(threshold, HANDLE_SURFACE_SNAP_DISTANCE);
        const outward = trial.axes?.z || IDENTITY_AXES.z;
        ({dx, dy, dz} = snapHandleAabbToPanelSurfaces(aabb, snapTargets, contactTh, {outward}));
    } else {
        const contactTh = mode === "live" ? Math.min(threshold, SNAP_LIVE_DISTANCE) : threshold;
        const alignTh = mode === "live"
            ? Math.min(alignThreshold, SNAP_LIVE_ALIGN_DISTANCE)
            : alignThreshold;
        // По умолчанию без rim: стык пластью в любом месте торца (в т.ч. середина).
        // Rim включается явно (seekEdges: true) и только в малом окне у края.
        const doSeek = seekEdges === true;
        ({dx, dy, dz} = snapAabbAgainstOthers(
            aabb,
            snapTargets,
            contactTh,
            alignTh,
            {
                seekEdges: doSeek,
                edgeSeek: mode === "live" ? SNAP_LIVE_EDGE_SEEK : SNAP_EDGE_SEEK,
            },
        ));
    }

    // Отлипание: не тянуть назад, если кадр ушёл от стыка.
    if (mode === "live" && previousLocalPosition) {
        const mx = localPosition.x - previousLocalPosition.x;
        const my = localPosition.y - previousLocalPosition.y;
        const mz = localPosition.z - previousLocalPosition.z;
        const snapMagSq = dx * dx + dy * dy + dz * dz;
        if (activeNodeLocal && snapMagSq > 1e-16) {
            const along = mx * dx + my * dy + mz * dz;
            if (along < -1e-8) {
                dx = 0;
                dy = 0;
                dz = 0;
            }
        } else {
            if (dx !== 0 && mx * dx < -1e-8) dx = 0;
            if (dy !== 0 && my * dy < -1e-8) dy = 0;
            if (dz !== 0 && mz * dz < -1e-8) dz = 0;
        }

        if (!handle && !activeNodeLocal) {
            // Одна «крупная» ось (контакт) + мелкие подтяжки к краю торца.
            const alignTh = Math.min(alignThreshold, SNAP_LIVE_ALIGN_DISTANCE);
            const companionLim = Math.max(alignTh * 2, SNAP_LIVE_ALIGN_DISTANCE);
            const axesDeltas = [
                {k: "x", d: dx},
                {k: "y", d: dy},
                {k: "z", d: dz},
            ].filter((a) => a.d !== 0);
            if (axesDeltas.length > 1) {
                axesDeltas.sort((a, b) => Math.abs(a.d) - Math.abs(b.d));
                const primary = axesDeltas[0].k;
                for (const a of axesDeltas) {
                    if (a.k === primary) continue;
                    if (Math.abs(a.d) > companionLim) {
                        if (a.k === "x") dx = 0;
                        if (a.k === "y") dy = 0;
                        if (a.k === "z") dz = 0;
                    }
                }
            }
        }
    }

    if (dx === 0 && dy === 0 && dz === 0) {
        return localPosition;
    }

    const world = cubeLocalToWorld(localPosition, cubeSize);
    return worldPointToCubeLocal(
        {x: world.x + dx, y: world.y + dy, z: world.z + dz},
        cubeSize,
    );
};

/**
 * Размещение по клику.
 * Панели: горизонтально + AABB-стык.
 * Ручки: ориентация по нормали поверхности + прилипание к панели.
 */
export const resolvePlacementPose = ({
    point,
    event = null,
    snapEnabled = true,
    cubeSize,
    others = [],
    detailData = null,
    /** Стартовая поза с бэка (paced-detail-modules), мм уже переведены в м */
    pacedPlacement = null,
    /** Ориентация, выбранная перед добавлением детали. */
    initialAxes = null,
}) => {
    const worldPoint = point instanceof THREE.Vector3
        ? point.clone()
        : new THREE.Vector3(point.x, point.y, point.z);

    const panels = (Array.isArray(others) ? others : []).filter(isPanelInstance);
    const handle = isHandleLike(detailData);

    if (handle) {
        const normal = worldNormalFromEvent(event);
        const axes = normal
            ? axesFromHandleMountNormal(normal)
            : cloneAxes(IDENTITY_AXES);
        let localPosition = worldPointToCubeLocal(worldPoint, cubeSize);

        if (snapEnabled) {
            localPosition = snapLocalToOtherDetails({
                inst: {instanceId: "__placing__", detailData, axes},
                localPosition,
                axes,
                others: panels,
                cubeSize,
                threshold: HANDLE_SURFACE_SNAP_DISTANCE,
                mode: "commit",
            });
        }

        return {
            localPosition,
            axes,
            euler: axesToEulerDegrees(axes),
            faceFlip: false,
        };
    }

    const axes = initialAxes
        ? cloneAxes(initialAxes)
        : pacedPlacement?.axes
            ? cloneAxes(pacedPlacement.axes)
            : cloneAxes(IDENTITY_AXES);
    // Якорь с бэка — если есть; иначе точка клика. Стыковка панелей только после драга.
    // При выборе ориентации в меню «+» всегда ставим в точку клика, не в сохранённую позу.
    const useBackendAnchor = Boolean(pacedPlacement?.localPosition && !initialAxes);
    const localPosition = useBackendAnchor
        ? {
            x: Number(pacedPlacement.localPosition.x) || 0,
            y: Number(pacedPlacement.localPosition.y) || 0,
            z: Number(pacedPlacement.localPosition.z) || 0,
        }
        : worldPointToCubeLocal(worldPoint, cubeSize);
    if (initialAxes) {
        const compensation = getInitialOrientationCompensation(axes, detailData);
        localPosition.x += compensation.x;
        localPosition.y += compensation.y;
        localPosition.z += compensation.z;
    }
    // faceFlip=false: локальный +Y = нормаль TOP (как вид в 2D).
    const faceFlip = initialAxes ? false : Boolean(pacedPlacement?.faceFlip);

    return {
        localPosition,
        axes,
        faceFlip,
        euler: axesToEulerDegrees(axes),
    };
};

/**
 * После драга: притянуть к контуру модуля и другим деталям. Ориентацию не меняем.
 */
export const resolveDragPose = ({
    inst,
    localPosition,
    axes,
    euler,
    snapEnabled = true,
    others = [],
    cubeSize,
    activeNodeLocal = null,
}) => {
    const nextAxes = cloneAxes(axes);
    let nextLocal = {...localPosition};
    const snapOthers = (Array.isArray(others) ? others : []).filter(isSnapTargetInstance);
    const handle = isHandleLike(inst?.detailData) || isHandleLike(inst?.detail);

    if (snapEnabled) {
        const threshold = handle
            ? HANDLE_SURFACE_SNAP_DISTANCE
            : (isEmbeddedModuleInstance(inst)
                ? MODULE_SHELL_SNAP_DISTANCE
                : SNAP_DISTANCE);
        nextLocal = snapLocalToOtherDetails({
            inst,
            localPosition: nextLocal,
            axes: nextAxes,
            others: snapOthers,
            cubeSize,
            threshold,
            mode: "commit",
            activeNodeLocal,
        });
    }

    return {
        localPosition: nextLocal,
        axes: nextAxes,
        euler: euler || undefined,
    };
};
